import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { gatorProtectActions } from '@/redux/modules/gatorProtect';
import {
  ActivitiesChart,
  PlansBanner,
  PROTECTION_CARD_TYPES,
  Summary,
} from './components';

import * as Styles from './GatorProtectHandler.styles';

import useLocale from '@/hooks/useLocale';
import withErrorBoundary from '@/utils/errorBoundary';
import ProtectionModal from './components/ProtectionModal';
import { isBRServer } from '@/utils/Validators/validation';
import { productsActions } from '@/redux/modules/products';
import { GATOR_PROTECT_ID } from './GatorProtect.enum';
import { formatCurrency } from '@/utils/Formatters/formatCurrency';
import { useHistory } from 'react-router';

const GatorProtectHandler = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    gatorProtect: gatorProtectLocale,
    routes: routesLocale,
  } = useLocale();
  const { loading, plans, planDetails } = useSelector(state => state.gatorProtect);
  const { loading: fastCheckoutLoading } = useSelector(state => state.fastCheckout);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showProtectionModal, setShowProtectionModal] = useState(false);
  const [activating, setActivating] = useState(false);
  const { consultedProducts } = useSelector(state => state.products);
  const gatorProtectProData = consultedProducts?.find(item => item.id === GATOR_PROTECT_ID.PRO);

  const mockPlans = useMemo(() => [
    // free example
    {
      shared_hosting_id: 345,
      shared_hosting_plan: 'Plano P',
      shared_hosting_domain: 'planofree.com',
      gatorprotect_hosting_id: '3123',
      gatorprotect_plan: 'Gator protect Trial',
      package_id: '2132',
      expire_days: 5,
      expire_date: '2026-04-01 00:00:00',
      is_pro: false,
      is_trial: false,
      trial_used: false,
    },
    // trial active example
    {
      shared_hosting_id: 346,
      shared_hosting_plan: 'Plano M',
      shared_hosting_domain: 'planotrial.com',
      gatorprotect_hosting_id: '3124',
      gatorprotect_plan: 'Gator protect Pro',
      package_id: '2133',
      expire_days: 10,
      expire_date: null,
      is_pro: false,
      is_trial: true,
      trial_used: false,
    },
    // trial expired example
    {
      shared_hosting_id: 346,
      shared_hosting_plan: 'Plano M',
      shared_hosting_domain: 'planotrialexpirado.com',
      gatorprotect_hosting_id: '3124',
      gatorprotect_plan: 'Gator protect Pro',
      package_id: '2133',
      expire_days: -3,
      expire_date: null,
      is_pro: false,
      is_trial: true,
      trial_used: false,
    },
    // pro active example
    {
      shared_hosting_id: 346,
      shared_hosting_plan: 'Plano M',
      shared_hosting_domain: 'planopro.com',
      gatorprotect_hosting_id: '3124',
      gatorprotect_plan: 'Gator protect Pro',
      package_id: '2133',
      expire_days: null,
      expire_date: null,
      is_pro: true,
      is_trial: false,
      trial_used: false,
    },
  ], []);

  const isDaysExpired = useMemo(() => (selectedPlan && selectedPlan.expire_days
    ? Number.isInteger(selectedPlan.expire_days) && selectedPlan.expire_days <= 0
    : false), [selectedPlan]);

  const isTrialActive = selectedPlan ? selectedPlan.is_trial && !isDaysExpired : false;
  const isTrialExpired = selectedPlan ? isDaysExpired : false;
  const isProActive = selectedPlan ? selectedPlan.is_pro : false;
  const isFreePlan = selectedPlan ? !selectedPlan.is_pro && !selectedPlan.is_trial : false;

  const mockPlanDetails = {
    summary: {
      malicious: 5,
      compromised: 3,
    },
    activity_detected: [
      {
        date: '2026-02-24',
        malicious: 2,
        compromised: 1,
      },
      {
        date: '2026-02-23',
        malicious: 1,
        compromised: 0,
      },
      {
        date: '2026-02-22',
        malicious: 1,
        compromised: 0,
      },
      {
        date: '2026-02-21',
        malicious: 1,
        compromised: 4,
      },
      {
        date: '2026-02-20',
        malicious: 3,
        compromised: 2,
      },
      {
        date: '2026-02-19',
        malicious: 0,
        compromised: 2,
      },
      {
        date: '2026-02-18',
        malicious: 2,
        compromised: 1,
      },
    ],
  };

  const useMock = false;
  const clientIsAA = selectedPlan?.trial_days === 365;
  const plansToDisplay = useMock ? mockPlans : plans;
  const planDetailsToDisplay = useMock ? mockPlanDetails : planDetails;

  const getProPrice = useCallback(() => {
    const gatorProtectProId = isBRServer ? 566 : 341;
    dispatch(productsActions.products.getProductById(gatorProtectProId));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getProPrice();
  }, [getProPrice]);

  const onComponentMount = useCallback(() => {
    dispatch(gatorProtectActions.plans.request());
  }, [dispatch]);

  const handleOnChangePlan = useCallback((plan) => {
    setSelectedPlan(plan);
    const last30Days = new Date();
    last30Days.setDate(last30Days.getDate() - 30);
    const formattedDate = last30Days.toISOString().slice(0, 10);
    dispatch(gatorProtectActions.planDetails.request({ hostingId: plan.shared_hosting_id, startDate: formattedDate }));
  }, [dispatch]);

  const handleToggleProtectionModal = useCallback(() => {
    setShowProtectionModal(previousState => !previousState);
  }, []);

  const protectionModalContent = useMemo(() => {
    const modalContent = gatorProtectLocale
      && gatorProtectLocale.summary
      && gatorProtectLocale.summary.protectionModal;

    if (!modalContent) {
      return null;
    }

    const getContent = () => {
      if (isTrialActive) {
        return {
          ...modalContent.trialActive,
          buttonLabel: modalContent.trialActive.buttonLabel(gatorProtectProData?.prices?.pricing?.monthly ? formatCurrency(gatorProtectProData?.prices?.pricing?.monthly) : '...'),
        };
      }

      if (isTrialExpired) {
        return modalContent.trialActive;
      }

      return {
        ...modalContent.trialActivation,
        title: modalContent.trialActivation.title(clientIsAA),
        subtitle: modalContent.trialActivation.subtitle(gatorProtectProData?.prices?.pricing?.monthly ? formatCurrency(gatorProtectProData?.prices?.pricing?.monthly) : '...'),
      };
    };

    const scenario = getContent();
    const parsedMessage = typeof scenario.message === 'function'
      ? scenario.message((selectedPlan && selectedPlan.expire_days) || 0)
      : scenario.message;

    const commonContent = modalContent.common || {};

    return {
      ...scenario,
      message: parsedMessage,
      checklistItems: scenario.checklistItems || commonContent.checklistItems || [],
      productTitle: commonContent.productTitle || gatorProtectLocale.banner.title,
      productSubtitle: commonContent.productSubtitle || gatorProtectLocale.banner.description,
      proBadgeLabel: commonContent.proBadgeLabel || gatorProtectLocale.banner.proBadgeLabel,
      mascotAlt: gatorProtectLocale.banner.img.alt,
    };
  }, [gatorProtectLocale, isTrialActive, selectedPlan, isTrialExpired, gatorProtectProData, clientIsAA]);

  const protectionCardType = useMemo(() => {
    if (isTrialActive) {
      return PROTECTION_CARD_TYPES.TRIAL_IS_ACTIVE;
    }

    if (isFreePlan || isTrialExpired) {
      return PROTECTION_CARD_TYPES.CHOOSE_HOW_TO_PROCEED;
    }

    return PROTECTION_CARD_TYPES.AUTOMATIC_PROTECTION_AVAILABLE;
  }, [isTrialActive, isFreePlan, isTrialExpired]);

  const hideProtectionCardButtons = false;

  const handleOnStartCleaning = useCallback(() => { }, []);

  const handleOnProtectionCardAction = useCallback((actionKey) => {
    switch (actionKey) {
      case 'startCleaning':
        handleOnStartCleaning();
        break;
      default:
        setActivating(actionKey);
        handleToggleProtectionModal();
    }
  }, [handleToggleProtectionModal, handleOnStartCleaning]);

  useEffect(() => {
    onComponentMount();
  }, [onComponentMount]);

  useEffect(() => {
    if (plansToDisplay.length > 0 && selectedPlan === null) {
      handleOnChangePlan(plansToDisplay[0]);
    }
  }, [plansToDisplay, selectedPlan, handleOnChangePlan]);

  const handleActivate = useCallback(() => {
    const redirectToInvoice = (invoiceId) => {
      history.push(`${routesLocale.invoices}${routesLocale.unpaid}/${invoiceId}`);
    };

    const dispatchActivate = ({ pid }) => {
      dispatch(gatorProtectActions.buy.request({
        id: pid,
        domain: selectedPlan?.shared_hosting_domain,
        redirectToInvoice,
      }));
    };

    switch (activating) {
      case 'activateTrial':
        dispatchActivate({ pid: GATOR_PROTECT_ID.TRIAL });
        break;
      case 'activateProtectionPro':
      case 'activateFullProtection':
        dispatchActivate({ pid: GATOR_PROTECT_ID.PRO });
        break;
      default:
        break;
    }
  }, [activating, dispatch, history, routesLocale?.invoices, routesLocale?.unpaid, selectedPlan]);

  return (
    <Styles.Container data-testid="gator-protect-handler">
      <PlansBanner
        content={gatorProtectLocale.banner}
        plans={plansToDisplay}
        loading={loading || plansToDisplay.length === 0}
        selectedPlan={selectedPlan}
        onChangePlan={handleOnChangePlan}
      />
      <Summary
        showProtectionCard={!isProActive}
        content={gatorProtectLocale.summary}
        alerts={{
          showTrialActiveMessage: isTrialActive,
          showCleaningInProgressMessage: false,
          showLimitedProtectionMessage: isFreePlan,
          showFullProtectionActiveMessage: isProActive,
          showExpiredProtectionMessage: isTrialExpired,
          showTeamInterventionMessage: false,
          showTeamInterventionResolvedMessage: false,
        }}
        protectionCardType={protectionCardType}
        hideProtectionCardButtons={hideProtectionCardButtons}
        onProtectionCardAction={handleOnProtectionCardAction}
        onOpenTicket={() => { }}
        trialDaysLeft={(selectedPlan && selectedPlan.expire_days) || 0}
        maliciousCodesRemoved={planDetailsToDisplay.summary.malicious}
        compromisedFiles={planDetailsToDisplay.summary.compromised}
        loading={loading}
        clientIsAA={clientIsAA}
      />
      <ActivitiesChart
        content={gatorProtectLocale.activitiesChart}
        data={planDetailsToDisplay.activity_detected.sort((a, b) => new Date(a.date) - new Date(b.date))}
        loading={loading}
      />
      {(showProtectionModal && protectionModalContent) && (
        <ProtectionModal
          content={protectionModalContent}
          onButtonClick={handleActivate}
          onClose={handleToggleProtectionModal}
          terms={gatorProtectLocale.terms}
          loading={loading || !consultedProducts?.length}
          fastCheckoutLoading={fastCheckoutLoading}
        />
      )}
    </Styles.Container>
  );
};

export default withErrorBoundary(GatorProtectHandler, 'GatorProtectHandler');
